import axios from 'axios';
import { v4 } from 'uuid';
import { transform, each, merge, isPlainObject, kebabCase, get, mergeWith, isArray, uniq } from 'lodash';
import Qs from 'qs';
import { deserialize, serialize, withPolymorphicType } from '@compeon-os/jsonapi-serializer';
export { withPolymorphicType } from '@compeon-os/jsonapi-serializer';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

const currentCookies = () => document.cookie.split(/;\s?/);

const currentCookiesHash = () => {
  const result = {};
  const cookies = currentCookies();

  for (const cookie of cookies) {
    const [key, value] = cookie.split(/=(.*)/);
    result[key] = value;
  }

  return result;
};

const getCookieValue = key => currentCookiesHash()[key];

const cookieExists = cookieName => currentCookies().some(cookie => cookie.trim().startsWith(`${cookieName}=`));

const cookieHelpers = {
  cookieExists,
  currentCookies,
  currentCookiesHash,
  getCookieValue
};

const AUTH_STATE_DEFAULT_NAME = "compeon-auth-state";
const AUTH_STATE_COOKIE_DEFAULT_OPTIONS = {
  "max-age": 900,
  path: "/",
  secure: true
};
const AUTH_REDIRECT_URL_KEY = "auth-redirect-uri";
const REFRESH_BEFORE_EXPIRY_IN_SECONDS = 10;
const LOGOUT_CHECK_INTERVAL_IN_MS = 1000;

function buildCookie(name, value, options) {
  const cookie = [`${name}=${value}`];
  each(options, (value, key) => {
    if (value === false || value == null) return;
    if (value === true) cookie.push(key);else cookie.push(`${key}=${value}`);
  });
  return cookie.join(";");
}

function cookieAuthState() {
  let config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    name = AUTH_STATE_DEFAULT_NAME,
    ...optionOverrides
  } = config;
  const options = transform(optionOverrides, (result, value, key) => {
    result[key.toLowerCase()] = value;
  }, { ...AUTH_STATE_COOKIE_DEFAULT_OPTIONS
  });

  function getCookieName(state) {
    return `${name}-${state}`;
  }

  function isValid(state) {
    // Avoid checking against cookies named `compeon-auth-state-undefined` or
    // `compeon-auth-state-null` by accident.
    return state != null && cookieHelpers.getCookieValue(getCookieName(state)) === "1";
  }

  function set(state) {
    const cookieName = getCookieName(state); // Delete cookie on current path first to avoid potential priority
    // conflicts.

    document.cookie = buildCookie(cookieName, "1", { ...options,
      "max-age": 0,
      path: false
    });
    document.cookie = buildCookie(cookieName, "1", options);
  }

  return {
    isValid,
    set
  };
}
function sessionStorageAuthState() {
  let config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    name = AUTH_STATE_DEFAULT_NAME
  } = config;
  return {
    isValid: state => sessionStorage.getItem(name) === state,
    set: state => sessionStorage.setItem(name, state)
  };
}
function getRedirectUrl() {
  return sessionStorage.getItem(AUTH_REDIRECT_URL_KEY);
}
function setRedirectUrl(url) {
  sessionStorage.setItem(AUTH_REDIRECT_URL_KEY, url);
}
function tokenRefreshTimeout(expiresIn) {
  return 1000 * (expiresIn - REFRESH_BEFORE_EXPIRY_IN_SECONDS);
}
function parseAuthQueryParams() {
  const search = document.location.search.substring(1);
  const params = new URLSearchParams(search);
  return {
    params,
    code: params.get("code"),
    state: params.get("state")
  };
}
function buildParams(obj) {
  const params = new URLSearchParams();

  for (const key in obj) params.append(key, obj[key]);

  return params;
}
function redirectToLogin(_ref) {
  let {
    baseUrl,
    clientId,
    redirectParams,
    authState
  } = _ref;
  const storedAuthState = v4();
  let redirectUri = removeAuthParams(document.location);

  if (redirectParams) {
    redirectUri = addRedirectParams(redirectUri, redirectParams);
  }

  authState.set(storedAuthState);
  setRedirectUrl(redirectUri);
  const params = buildParams({
    state: storedAuthState,
    redirect_uri: redirectUri,
    client_id: clientId,
    response_type: "code"
  });
  const nextLocation = new URL("/oauth2/authorization", baseUrl);
  nextLocation.search = params;
  window.location.assign(nextLocation.toString());
}
function tokenRefreshParams(refreshCode) {
  return buildParams({
    grant_type: "refresh_code",
    refresh_code: refreshCode
  });
}
function initialTokenParams(code, clientId) {
  return buildParams({
    grant_type: "authorization_code",
    client_id: clientId,
    redirect_uri: getRedirectUrl(),
    code
  });
}
function parseTokenResponse(response) {
  const {
    access_token: token,
    refresh_token: refreshToken,
    identity_token: identityToken,
    expires_in: expiresIn
  } = response;
  const now = new Date(Date.now());
  const expiresAt = now.setSeconds(now.getSeconds() + expiresIn);
  return {
    token,
    refreshToken,
    expiresIn,
    expiresAt,
    identityToken
  };
}
function completeLogout(_ref2) {
  let {
    baseUrl,
    loggedInCookieName,
    loggedInCookieDomain
  } = _ref2;
  document.cookie = buildCookie(loggedInCookieName, "0", {
    "max-age": 0,
    domain: loggedInCookieDomain,
    path: "/",
    secure: true
  });
  const redirectUrl = removeAuthParams(document.location);
  const logoutParams = new URLSearchParams({
    redirect_uri: redirectUrl
  });
  const logoutUrl = new URL("/oauth2/logout", baseUrl);
  logoutUrl.search = logoutParams;
  window.location.assign(logoutUrl);
}
function checkLogout(logout, _ref3) {
  let {
    loggedInCookieName,
    interval = LOGOUT_CHECK_INTERVAL_IN_MS
  } = _ref3;

  const logoutIfLoggedInCookieAbsent = () => {
    if (!cookieHelpers.cookieExists(loggedInCookieName)) logout();
  };

  return setInterval(logoutIfLoggedInCookieAbsent, interval);
}
const transformTokenResponse = [JSON.parse, parseTokenResponse];

function addRedirectParams(uri, redirectParams) {
  const parsedUri = new URL(uri);

  for (const [name, value] of redirectParams) {
    parsedUri.searchParams.set(name, value);
  }

  return parsedUri.toString();
}

function removeAuthParams(uri) {
  const parsedUri = new URL(uri);
  parsedUri.searchParams.delete("state");
  parsedUri.searchParams.delete("code");
  return parsedUri.toString();
}

const DEFAULT_PARAMS = Object.freeze({
  page: {
    number: 1,
    size: 25
  }
});
const prepareParams = requestConfig => {
  const params = {};
  if (!requestConfig.noDefaultParams) merge(params, DEFAULT_PARAMS);
  merge(params, requestConfig.options);
  const {
    include
  } = params;
  if (include && include.length) params.include = include.join(",");
  requestConfig.params = params;
  return requestConfig;
};
const deserializeData = data => {
  return data ? JSON.parse(data) : null;
};
const handleError = console.log;
const serializeParams = params => Qs.stringify(params, {
  arrayFormat: "brackets"
});
const deepKebabTransform = data => {
  if (Array.isArray(data)) return data.map(i => deepKebabTransform(i));
  if (!isPlainObject(data)) return data;
  const result = {};

  for (const key in data) result[kebabCase(key)] = deepKebabTransform(data[key]);

  return result;
};
const removeToken = (data, headers) => delete headers.Authorization;
const cookieAuthenticatedConfig = {
  transformRequest: removeToken,
  withCredentials: true
};

const DEFAULT_AXIOS_CONFIG = Object.freeze({
  headers: {
    Accept: "application/json",
    "Content-Type": "application/vnd.api+json"
  },
  paramsSerializer: serializeParams,
  transformRequest: [deepKebabTransform, JSON.stringify],
  transformResponse: [deserializeData]
});
function axiosConfig(config) {
  const mergedConfig = { ...DEFAULT_AXIOS_CONFIG,
    ...config
  };

  if (config.transformResponse) {
    mergedConfig.transformResponse = DEFAULT_AXIOS_CONFIG.transformResponse.concat(config.transformResponse);
  }

  if (config.normalize) mergedConfig.transformResponse.push(deserialize());
  return mergedConfig;
}

class Resource {
  constructor(client) {
    var _this = this;

    _defineProperty(this, "get", function () {
      return _this.httpClient.get(...arguments);
    });

    _defineProperty(this, "del", function () {
      return _this.httpClient.delete(...arguments);
    });

    _defineProperty(this, "head", function () {
      return _this.httpClient.head(...arguments);
    });

    _defineProperty(this, "options", function () {
      return _this.httpClient.options(...arguments);
    });

    _defineProperty(this, "post", function () {
      return _this.httpClient.post(...arguments);
    });

    _defineProperty(this, "put", function () {
      return _this.httpClient.put(...arguments);
    });

    _defineProperty(this, "patch", function () {
      return _this.httpClient.patch(...arguments);
    });

    this.client = client;
    this.httpClient = client.httpClient;
    this.all = this.all.bind(this);
    this.find = this.find.bind(this);
    this.create = this.create.bind(this);
    this.update = this.update.bind(this);
    this.delete = this.delete.bind(this);
    this.memberGet = this.memberGet.bind(this);
    this.memberPost = this.memberPost.bind(this);
  }

  all() {
    let {
      options
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return this.get(this.resourcePath, {
      options
    });
  }

  find(_ref) {
    let {
      id,
      options
    } = _ref;
    return this.get(`${this.resourcePath}/${id}`, {
      options
    });
  }

  memberGet(_ref2) {
    let {
      id,
      path,
      options
    } = _ref2;
    return this.get(`${this.resourcePath}/${id}/${path}`, {
      options
    });
  }

  memberPost(_ref3) {
    let {
      id,
      path,
      data,
      options
    } = _ref3;
    return this.post(`${this.resourcePath}/${id}/${path}`, data, {
      options
    });
  }

  create(_ref4) {
    let {
      data,
      options
    } = _ref4;
    return this.post(this.resourcePath, this.serializer(data), {
      options
    });
  }

  update(_ref5) {
    let {
      id,
      data,
      options
    } = _ref5;
    return this.patch(`${this.resourcePath}/${id}`, this.serializer(data), {
      options
    });
  }

  delete(_ref6) {
    let {
      id,
      options
    } = _ref6;
    return this.del(`${this.resourcePath}/${id}`, {
      options
    });
  }

}

var Resource$1 = Resource;

class LegacyDocumentResource extends Resource$1 {
  constructor() {
    var _superprop_getAll = () => super.all,
        _this;

    super(...arguments);
    _this = this;

    _defineProperty(this, "all", function () {
      let {
        options
      } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return _superprop_getAll().call(_this, {
        options: merge({
          page: {
            size: 999
          }
        }, options)
      });
    });

    _defineProperty(this, "download", _ref => {
      let {
        id,
        options,
        fileName
      } = _ref;
      return this.get(`${this.resourcePath}/${id}/download`, {
        noDefaultParams: true,
        options
      }).then(response => {
        const location = get(response, ["data", "data", "attributes", "location"]);
        const link = document.createElement("a");
        document.body.appendChild(link);
        link.href = location;
        link.download = fileName || "Dokument";
        link.click();
        document.body.removeChild(link);
        return response;
      });
    });
  }

}

var LegacyDocumentResource$1 = LegacyDocumentResource;

const adminDocumentSchema = {
  relationships: {
    tender: {
      type: "tenders"
    },
    uploader: {
      type: "users"
    }
  }
};

const attachmentSchema = {
  relationships: {
    actualUploader: {
      type: "users"
    },
    context: {
      type: "inquiries"
    },
    document: {
      type: "documents"
    },
    owner: {
      type: "companies"
    },
    uploader: {
      type: "users"
    }
  }
};

const bankTagSchema = {
  relationships: {
    bankTagCategory: {
      type: "bank-tag-categories"
    }
  }
};

const bankSchema = {
  relationships: {
    bankTags: {
      type: "bank-tags",
      ...bankTagSchema
    },
    parentBank: {
      type: "banks"
    },
    bankGroup: {
      type: "bank-groups"
    }
  }
};

const bankGroupSchema = {
  relationships: {
    bank: {
      type: "banks"
    },
    productConditions: {
      type: "product-conditions"
    }
  }
};

const bankGroupCandidateSchema = {
  relationships: {
    bankGroup: {
      type: "bank-groups"
    },
    inquiry: {
      type: "inquiries"
    }
  }
};

const bankInquiryNoteSchema = {
  relationships: {
    bank: {
      type: "banks"
    },
    inquiry: {
      type: "inquiries"
    },
    user: {
      type: "users"
    }
  }
};

const bankVisibilityParameterSetSchema = {
  relationships: {
    bank: {
      type: "banks"
    },
    excludedMultiplierOrganizations: {
      type: "multiplier-organizations"
    }
  }
};

const bankVisibilitySchema = {
  relationships: {
    bank: {
      type: "banks"
    },
    inquiry: {
      type: "inquiries"
    },
    tender: {
      type: "tenders"
    }
  }
};

const companyMergeSchema = {
  relationships: {
    sourceCompany: {
      type: "companies"
    }
  }
};

const creditInformationSourceSchema = {
  relationships: {
    company: {
      type: "companies"
    }
  }
};

const datacenterSchema = {
  relationships: {
    context: {
      type: "inquiries"
    },
    creator: {
      type: "users"
    },
    documents: {
      type: "polymorphic"
    }
  }
};

const documentAccessesSchema = {
  relationships: {
    organization: {
      type: "banks"
    }
  }
};

const emailVerificationRequestSchema = {
  relationships: {
    context: {
      type: "polymorphic"
    }
  }
};

const employeeSchema = {
  relationships: {
    inviter: {
      type: "users"
    },
    organization: {
      type: "polymorphic"
    },
    user: {
      type: "users"
    }
  }
};

const estimatedOfferConditionsSchema = {
  relationships: {
    inquiry: {
      type: "inquiries"
    }
  }
};

const externalBusinessRelationshipSchema = {
  relationships: {
    bank: {
      type: "banks"
    },
    company: {
      type: "companies"
    }
  }
};

const individualAccessSchema = {
  relationships: {
    inviter: {
      type: "users"
    },
    membership: {
      type: "memberships"
    },
    tender: {
      type: "tenders"
    },
    inquiry: {
      type: "inquiries"
    },
    user: {
      type: "users"
    }
  }
};

const inquiryCompleteEventSchema = {
  relationships: {
    offersToAccept: {
      type: "offers"
    }
  }
};

const indicativeOfferForwardingSchema = {
  relationships: {
    offers: {
      type: "connect-offers"
    }
  }
};

const indicativeOfferSchema = {
  relationships: {
    inquiry: {
      type: "inquiries"
    },
    requestedForUser: {
      type: "users"
    }
  }
};

const userSchema = {
  relationships: {
    bank: {
      type: "banks"
    },
    firstInvitedByUser: {
      type: "users"
    },
    multiplierOrganization: {
      type: "multiplier-organizations"
    }
  }
};

const inquirySchema = {
  relationships: {
    bankNotes: {
      type: "bank-inquiry-notes"
    },
    chosenSubsidyProducts: {
      type: "subsidy-products"
    },
    company: {
      type: "companies"
    },
    collaterals: {
      type: "collaterals"
    },
    contact: {
      type: "users",
      ...userSchema
    },
    multiplierOrganization: {
      type: "multiplier-organizations"
    },
    subsidyRequest: {
      type: "subsidy-request"
    },
    user: {
      type: "users",
      ...userSchema
    }
  }
};

const lifecycleLogEntrySchema = {
  relationships: {
    loggable: {
      type: "inquiries"
    },
    organization: {
      type: "companies"
    }
  }
};

const membershipSchema = {
  relationships: {
    organization: {
      type: "polymorphic"
    },
    user: {
      type: "users"
    }
  }
};

const multiplierOrganizationSchema = {
  relationships: {
    salesContact: {
      type: "users"
    }
  }
};

const offerSchema = {
  relationships: {
    bank: {
      type: "banks"
    },
    creator: {
      type: "users"
    },
    inquiry: {
      type: "inquiries"
    },
    supplementaryOfferConditions: {
      type: "supplementary-offer-conditions"
    }
  }
};

const productConditionsSchema = {
  relationships: {
    bank: {
      type: "banks"
    },
    bankGroup: {
      type: "bank-groups"
    }
  }
};

const questionnaireItemSchema = {
  relationships: {
    questionnaire: {
      type: "questionnaires"
    }
  }
};

const questionnaireSchema = {
  relationships: {
    bank: {
      type: "banks"
    },
    inquiry: {
      type: "inquiries"
    },
    sourceTemplate: {
      type: "questionnaires"
    }
  }
};

const requestedDocumentKindSchema = {
  relationships: {
    inquiry: {
      type: "inquiry"
    },
    documentKind: {
      type: "document-kind"
    }
  }
};

const requestedDocumentsSetSchema = {
  relationships: {
    bank: {
      type: "banks"
    },
    documentKinds: {
      type: "document-kinds"
    }
  }
};

const successFeeSchema = {
  relationships: {
    acceptingUser: {
      type: "users"
    },
    inquiry: {
      type: "inquiries"
    },
    offer: {
      type: "offers"
    }
  }
};

const subsidyRequestSchema = {
  relationships: {
    inquiry: {
      type: "inquiries"
    },
    tender: {
      type: "tenders"
    }
  }
};

const taskSchema = {
  relationships: {
    inquiry: {
      type: "inquiries"
    },
    organization: {
      type: "polymorphic"
    }
  }
};

const taxAccountantSchema = {
  relationships: {
    inquiry: {
      type: "inquiries"
    }
  }
};

const tenderSchema = {
  relationships: {
    company: {
      type: "companies"
    },
    contact: {
      type: "contacts"
    },
    creator: {
      type: "users"
    },
    inquiry: {
      type: "inquiries"
    },
    "multiplier-organization": {
      type: "multiplier-organizations"
    },
    notes: {
      type: "notes"
    },
    offers: {
      type: "offers"
    },
    "sales-employee": {
      type: "users"
    },
    "source-tender": {
      type: "tenders"
    },
    "tender-details": {
      type: "tender-details"
    }
  }
};

const adminDocumentSerializer = serialize("admin-documents", adminDocumentSchema);
const attachmentSerializer = serialize("attachments", attachmentSchema);
const authenticationSerializer = serialize("logins");
const bankCodeSerializer = serialize("bank-codes");
const bankGroupSerializer = serialize("bank-groups", bankGroupSchema);
const bankGroupCandidateSerializer = serialize("bank-group-candidates", bankGroupCandidateSchema);
const bankSerializer = serialize("banks", bankSchema);
const bankTagSerializer = serialize("bank-tags", bankTagSchema);
const bankVisibilityParameterSetSerializer = serialize("bank-visibility-parameter-sets", bankVisibilityParameterSetSchema);
const bankVisibilitySerializer = serialize("bank-visibilities", bankVisibilitySchema);
serialize("collaterals");
const companyMergeSerializer = serialize("company-merges", companyMergeSchema);
const companySerializer = serialize("companies");
const creditInformationSourceSerializer = serialize("credit-information-sources", creditInformationSourceSchema);
const datacenterSerializer = serialize("datacenters", datacenterSchema);
const documentAccessSerializer = serialize("documents", documentAccessesSchema);
const documentKindSerializer = serialize("document-kinds");
const documentRelationshipSerializer = serialize("documents");
const documentSerializer = serialize("documents");
const emailVerificationSerializer = serialize("email-verifications");
const emailVerificationRequestSerializer = serialize("email-verification-requests", emailVerificationRequestSchema);
const employeeSerializer = serialize("employees", employeeSchema);
const estimatedOfferConditionsSerializer = serialize("estimated-offer-conditions", estimatedOfferConditionsSchema);
const externalBusinessRelationshipSerializer = serialize("external-business-relationships", externalBusinessRelationshipSchema);
const indicativeOfferSerializer = serialize("indicative-offers-request", indicativeOfferSchema);
const indicativeOfferForwardingSerializer = serialize("indicative-offers-forwardings", indicativeOfferForwardingSchema);
const individualAccessSerializer = serialize("individual-accesses", individualAccessSchema);
const bankInquiryNoteSerializer = serialize("banks", bankInquiryNoteSchema);
const inquiryCompleteEventSerializer = serialize("inquiry-complete-events", inquiryCompleteEventSchema);
const inquirySerializer = serialize("inquiries", inquirySchema);
const inquiryExternalStateSerializer = serialize("inquiry-states");
const lifecycleLogEntrySerializer = serialize("lifecycle-log-entries", lifecycleLogEntrySchema);
const membershipSerializer = serialize("memberships", membershipSchema);
const multiplierOrganizationSerializer = serialize("multiplier-organizations", multiplierOrganizationSchema);
const offerSerializer = serialize("offers", offerSchema);
const productConditionsSerializer = serialize("product-conditions", productConditionsSchema);
const questionnaireItemSerializer = serialize("questionnaire-items", questionnaireItemSchema);
const questionnaireSerializer = serialize("questionnaires", questionnaireSchema);
const requestedDocumentKindSerializer = serialize("requested-document-kinds", requestedDocumentKindSchema);
const requestedDocumentsSetSerializer = serialize("requested-documents-sets", requestedDocumentsSetSchema);
const subsidyRequestSerializer = serialize("subsidy-requests", subsidyRequestSchema);
const successFeeAcceptanceSerializer = serialize("success-fee-acceptances");
const successFeeSerializer = serialize("success-fees", successFeeSchema);
const taskSerializer = serialize("tasks", taskSchema);
const taxAccountantSerializer = serialize("tax-accountants", taxAccountantSchema);
const telephoneVerificationSerializer = serialize("telephone-verifications");
const tenderSerializer = serialize("tenders", tenderSchema);
const termsAndConditionsVersionsSerializer = serialize("terms-and-conditions-versions");
const userSerializer = serialize("users", userSchema);
const vehicleSerializer = serialize("vehicles");
const virtualPathSerializer = serialize("virtual-paths");

/**
 * AdminDocument resource
 */

class AdminDocument extends LegacyDocumentResource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "allForInquiry", _ref => {
      let {
        inquiryId,
        options
      } = _ref;
      return this.all({
        options: merge({
          filter: {
            "inquiry-id": inquiryId
          },
          include: ["uploader"]
        }, options)
      });
    });

    _defineProperty(this, "allForTender", _ref2 => {
      let {
        tenderId,
        options
      } = _ref2;
      return this.all({
        options: merge({
          filter: {
            "tender-id": tenderId
          },
          include: ["uploader"]
        }, options)
      });
    });
  }

  /**
   * @private
   * @returns {string} AdminDocument resource path
   */
  get resourcePath() {
    return "v1/admin-documents";
  }
  /**
   * @private
   * @returns {function} AdminDocument serializer
   */


  get serializer() {
    return adminDocumentSerializer;
  }
  /**
   * Get all admin documents for a specific inquiry
   * @param {object} config
   * @param {string} config.inquiryId The Id of the inquiry to get all admin documents for
   * @param {object} config.options Options that are forwarded to the http client
   * @returns {Promise{object}}
   */


}

var AdminDocument$1 = AdminDocument;

/**
 * Attachment resource
 */

class Attachment extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "resourcePath", (contextType, contextId) => `v1/${contextType}/${contextId}/attachments`);

    _defineProperty(this, "find", _ref => {
      let {
        contextType,
        contextId,
        id,
        options
      } = _ref;
      return this.get(`${this.resourcePath(contextType, contextId)}/${id}`, {
        options
      });
    });

    _defineProperty(this, "all", _ref2 => {
      let {
        contextType,
        contextId,
        options
      } = _ref2;
      return this.get(this.resourcePath(contextType, contextId), {
        options: merge({
          page: {
            size: 999
          }
        }, options)
      });
    });

    _defineProperty(this, "create", _ref3 => {
      let {
        contextType,
        contextId,
        data,
        options
      } = _ref3;
      return this.post(this.resourcePath(contextType, contextId), this.serializer(data), {
        options
      });
    });

    _defineProperty(this, "delete", _ref4 => {
      let {
        contextType,
        contextId,
        id,
        options
      } = _ref4;
      return this.del(`${this.resourcePath(contextType, contextId)}/${id}`, {
        options
      });
    });

    _defineProperty(this, "update", _ref5 => {
      let {
        contextType,
        contextId,
        id,
        data,
        options
      } = _ref5;
      return this.patch(`${this.resourcePath(contextType, contextId)}/${id}`, this.serializer(data), {
        options
      });
    });

    _defineProperty(this, "getDocumentAccess", _ref6 => {
      let {
        contextType,
        contextId,
        attachmentId,
        bankId,
        options
      } = _ref6;
      return this.get(`${this.resourcePath(contextType, contextId)}/${attachmentId}/accesses`, documentAccessSerializer({
        organization: {
          id: bankId
        }
      }), {
        noDefaultParams: true,
        options
      });
    });

    _defineProperty(this, "createDocumentAccess", _ref7 => {
      let {
        contextType,
        contextId,
        attachmentId,
        bankId,
        options
      } = _ref7;
      return this.post(`${this.resourcePath(contextType, contextId)}/${attachmentId}/accesses`, documentAccessSerializer({
        organization: {
          id: bankId
        }
      }), {
        options
      });
    });

    _defineProperty(this, "deleteDocumentAccess", _ref8 => {
      let {
        contextType,
        contextId,
        attachmentId,
        id,
        options
      } = _ref8;
      return this.del(`${this.resourcePath(contextType, contextId)}/${attachmentId}/accesses/${id}`, {
        options
      });
    });
  }

  /**
   * @private
   * @returns {function} attachment serializer
   */
  get serializer() {
    return attachmentSerializer;
  }
  /**
   * Get a specific attachment
   * @param {object} config
   * @param {string} config.contextType the context in which the attachment exists (e.g. inquiry)
   * @param {string} config.contextId Id of the specified context (e.g. inquiryId)
   * @param {string} config.id Id of the attachment
   * @param {object} config.options Options that are forwarded to the http client
   * @returns {Promise{object}}
   */


}

var Attachment$1 = Attachment;

/**
 * Authentication Resource
 */

class Authentication extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "fetchTokens", _ref => {
      let {
        code
      } = _ref;
      return axios({ ...this.tokenRequest,
        data: initialTokenParams(code, this.authConfig.clientId)
      });
    });

    _defineProperty(this, "refreshTokens", _ref2 => {
      let {
        refreshCode
      } = _ref2;
      return axios({ ...this.tokenRequest,
        data: tokenRefreshParams(refreshCode)
      });
    });

    _defineProperty(this, "isSignedInWithCookie", () => this.get(this.resourcePath, cookieAuthenticatedConfig));

    _defineProperty(this, "getCookie", () => this.get("v1/identity/obtain-cookie", {
      withCredentials: true
    }));
  }

  /**
   * @private
   * @returns {string} session resource path
   */
  get resourcePath() {
    return "v1/session";
  }
  /**
   * @private
   * @returns {function} authentication serializer
   */


  get serializer() {
    return authenticationSerializer;
  }
  /**
   * @private
   * @returns {object} current auth config object
   */


  get authConfig() {
    return this.client.config.authConfig;
  }
  /**
   * @private
   * @returns {object} OAuth2 token url
   */


  get tokenUrl() {
    return new URL("/oauth2/token", this.authConfig.baseUrl);
  }
  /**
   * @private
   * @returns {object} config for the token request
   */


  get tokenRequest() {
    return {
      method: "post",
      url: this.tokenUrl,
      transformResponse: transformTokenResponse,
      validateStatus: status => status >= 200 && (status < 400 || status === 401)
    };
  }
  /**
   * fetch OAuth tokens using the authorizaton code
   * @param {object} config
   * @param {string} config.code OAuth2 authorization code
   * @returns {Promise{object}}
   */


}

var Authentication$1 = Authentication;

/**
 * Bank resource
 */

class Bank extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "allEmployees", _ref => {
      let {
        bankId,
        options
      } = _ref;
      return this.get(`${this.resourcePath}/${bankId}/employees`, {
        options
      });
    });

    _defineProperty(this, "inviteEmployee", _ref2 => {
      let {
        bankId,
        inviterId,
        data,
        options
      } = _ref2;
      return this.post(`${this.resourcePath}/${bankId}/employees`, employeeSerializer({ ...data,
        inviter: {
          id: inviterId
        },
        organization: withPolymorphicType("banks", {
          id: bankId
        })
      }), {
        options
      });
    });

    _defineProperty(this, "deleteEmployee", _ref3 => {
      let {
        bankId,
        id,
        options
      } = _ref3;
      return this.del(`${this.resourcePath}/${bankId}/employees/${id}`, {
        options
      });
    });

    _defineProperty(this, "updateEmployee", _ref4 => {
      let {
        bankId,
        data,
        id,
        userId,
        options
      } = _ref4;
      return this.patch(`${this.resourcePath}/${bankId}/employees/${id}`, employeeSerializer({ ...data,
        organization: withPolymorphicType("banks", {
          id: bankId
        }),
        user: {
          id: userId
        }
      }), {
        noDefaultParams: true,
        options
      });
    });

    _defineProperty(this, "allThatCanSeeTender", _ref5 => {
      let {
        tenderId,
        options
      } = _ref5;
      return this.all({
        options: merge({
          filter: {
            "can-see-tender": tenderId
          }
        }, options)
      });
    });

    _defineProperty(this, "find", _ref6 => {
      let {
        id,
        options
      } = _ref6;
      return super.find({
        id,
        options: merge({
          include: ["bank-tags"]
        }, options)
      });
    });

    _defineProperty(this, "update", _ref7 => {
      let {
        id,
        data,
        options
      } = _ref7;
      return super.update({
        id,
        data,
        options: merge({
          include: ["bank-tags"]
        }, options)
      });
    });

    _defineProperty(this, "requestedDocuments", _ref8 => {
      let {
        bankId,
        options
      } = _ref8;
      return this.get(`${this.resourcePath}/${bankId}/requested-documents`, {
        options
      });
    });
  }

  /**
   * @private
   * @returns {string} Bank resource path
   */
  get resourcePath() {
    return "v1/banks";
  }
  /**
   * @private
   * @returns {function} attachment serializer
   */


  get serializer() {
    return bankSerializer;
  }
  /**
   * Get all employees for a specific bank
   * @param {object} config
   * @param {string} config.bankId the bank to get the employees for
   * @param {object} config.options Options that are forwarded to the http client
   * @returns {Promise{object}}
   */


}

var Bank$1 = Bank;

/**
 * BankCode resource
 */

class BankCode extends Resource$1 {
  constructor() {
    var _superprop_getAll = () => super.all,
        _this;

    super(...arguments);
    _this = this;

    _defineProperty(this, "all", function () {
      let {
        options
      } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return _superprop_getAll().call(_this, {
        options: merge({
          page: {
            size: 9999
          }
        }, options)
      });
    });
  }

  /**
   * @private
   * @returns {string} BankCode resource path
   */
  get resourcePath() {
    return "v1/bank-codes";
  }
  /**
   * @private
   * @returns {function} BankCode serializer
   */


  get serializer() {
    return bankCodeSerializer;
  }
  /**
   * Get all BankCodes
   * @param {object} config
   * @param {object} config.options Options that are forwarded to the http client
   * @returns {Promise{object}}
   */


}

var BankCode$1 = BankCode;

/**
 * BankGroup resource
 */

class BankGroup extends Resource$1 {
  constructor() {
    var _superprop_getAll = () => super.all,
        _this;

    super(...arguments);
    _this = this;

    _defineProperty(this, "all", function () {
      let {
        options
      } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return _superprop_getAll().call(_this, {
        options: merge({
          page: {
            size: 999
          }
        }, options)
      });
    });
  }

  /**
   * @private
   * @returns {string} BankGroup resource path
   */
  get resourcePath() {
    return "v1/bank-groups";
  }
  /**
   * @private
   * @returns {function} BankGroup serializer
   */


  get serializer() {
    return bankGroupSerializer;
  }
  /**
   * Get all BankGroups
   * @param {object} config
   * @param {object} config.options Options that are forwarded to the http client
   * @returns {Promise{object}}
   */


}

var BankGroup$1 = BankGroup;

/**
 * BankGroupCandidate resource
 */

class BankGroupCandidate extends Resource$1 {
  /**
   * @private
   * @returns {string} BankGroupCandidate resource path
   */
  get resourcePath() {
    return "v1/bank-group-candidates";
  }
  /**
   * @private
   * @returns {function} BankGroupCandidate serializer
   */


  get serializer() {
    return bankGroupCandidateSerializer;
  }

}

var BankGroupCandidate$1 = BankGroupCandidate;

/**
 * BankInquiryNote resource
 */

class BankInquiryNote extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "resourcePath", inquiryId => `v1/inquiries/${inquiryId}/notes`);

    _defineProperty(this, "find", _ref => {
      let {
        inquiryId,
        id,
        options
      } = _ref;
      return this.get(`${this.resourcePath(inquiryId)}/${id}`, {
        options
      });
    });

    _defineProperty(this, "all", _ref2 => {
      let {
        inquiryId,
        options
      } = _ref2;
      return this.get(this.resourcePath(inquiryId), {
        options: merge({
          page: {
            size: 999
          }
        }, options)
      });
    });

    _defineProperty(this, "create", _ref3 => {
      let {
        inquiryId,
        data,
        options
      } = _ref3;
      return this.post(this.resourcePath(inquiryId), this.serializer(data), {
        options
      });
    });

    _defineProperty(this, "delete", _ref4 => {
      let {
        inquiryId,
        id,
        options
      } = _ref4;
      return this.del(`${this.resourcePath(inquiryId)}/${id}`, {
        options
      });
    });

    _defineProperty(this, "update", _ref5 => {
      let {
        inquiryId,
        id,
        data,
        options
      } = _ref5;
      return this.patch(`${this.resourcePath(inquiryId)}/${id}`, this.serializer(data), {
        options
      });
    });
  }

  /**
   * @private
   * @returns {function} bankInquiryNoteSerializer
   */
  get serializer() {
    return bankInquiryNoteSerializer;
  }
  /**
   * Get a specific bankInquiryNote
   * @param {object} config
   * @param {string} config.inquiryId id of the inquiry that the note is attached to
   * @param {string} config.id Id of the note
   * @param {object} config.options Options that are forwarded to the http client
   * @returns {Promise{object}}
   */


}

var BankInquiryNote$1 = BankInquiryNote;

/**
 * BankTag resource
 */

class BankTag extends Resource$1 {
  constructor() {
    var _superprop_getAll = () => super.all,
        _this;

    super(...arguments);
    _this = this;

    _defineProperty(this, "all", function () {
      let {
        options
      } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return _superprop_getAll().call(_this, {
        options: merge({
          page: {
            size: 999
          }
        }, options)
      });
    });
  }

  /**
   * @private
   * @returns {string} BankTag resource path
   */
  get resourcePath() {
    return "v1/bank-tags";
  }
  /**
   * @private
   * @returns {string} BankTag resource path
   */


  get serializer() {
    return bankTagSerializer;
  }
  /**
   * Get all BankTags
   * @param {object} config
   * @param {object} config.options Options that are forwarded to the http client
   * @returns {Promise{object}}
   */


}

var BankTag$1 = BankTag;

const mergeWithConcat = (object, source) => {
  return mergeWith(object, source, (a, b) => {
    if (isArray(a)) return uniq(a.concat(b));
  });
};

var mergeWithConcat$1 = mergeWithConcat;

/**
 * BankVisibility resource
 */

class BankVisibility extends Resource$1 {
  constructor() {
    var _superprop_getAll = () => super.all,
        _this;

    super(...arguments);
    _this = this;

    _defineProperty(this, "all", function () {
      let {
        options
      } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return _superprop_getAll().call(_this, {
        options: mergeWithConcat$1({
          include: ["bank", "inquiry", "tender"],
          page: {
            size: 999
          }
        }, options)
      });
    });
  }

  /**
   * @private
   * @returns {string} BankVisibility resource path
   */
  get resourcePath() {
    return "v1/bank-visibilities";
  }
  /**
   * @private
   * @returns {function} BankVisibility serializer
   */


  get serializer() {
    return bankVisibilitySerializer;
  }
  /**
   * Get all BankVisibilities
   * @param {object} config
   * @param {object} config.options Options that are forwarded to the http client
   * @returns {Promise{object}}
   */


}

var BankVisibility$1 = BankVisibility;

/**
 * BankVisibilityParameterSet resource
 */

class BankVisibilityParameterSet extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "all", _ref => {
      let {
        bankId,
        options
      } = _ref;
      return this.get(`v1/banks/${bankId}/visibility-parameter-sets`, {
        options
      });
    });
  }

  /**
   * @private
   * @returns {string} BankVisibilityParameterSet resource path
   */
  get resourcePath() {
    return "v1/bank-visibility-parameter-sets";
  }
  /**
   * @private
   * @returns {function} BankVisibilityParameterSet serializer
   */


  get serializer() {
    return bankVisibilityParameterSetSerializer;
  }
  /**
   * Get all BankVisibilityParameterSets
   * @param {object} config
   * @param {string} config.bankId bankId to fetch the parametersets for
   * @param {object} config.options Options that are forwarded to the http client
   * @returns {Promise{object}}
   */


}

var BankVisibilityParameterSet$1 = BankVisibilityParameterSet;

class Company extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "allEmployees", _ref => {
      let {
        id,
        options
      } = _ref;
      return this.get(`${this.resourcePath}/${id}/employees`, {
        options
      });
    });

    _defineProperty(this, "inviteEmployee", _ref2 => {
      let {
        companyId,
        employee,
        inviterId,
        options
      } = _ref2;
      return this.post(`${this.resourcePath}/${companyId}/employees`, employeeSerializer({ ...employee,
        inviter: {
          id: inviterId
        },
        organization: withPolymorphicType("companies", {
          id: companyId
        })
      }), {
        noDefaultParams: true,
        options
      });
    });

    _defineProperty(this, "updateEmployee", _ref3 => {
      let {
        companyId,
        data,
        id,
        userId,
        options
      } = _ref3;
      return this.patch(`${this.resourcePath}/${companyId}/employees/${id}`, employeeSerializer({ ...data,
        organization: withPolymorphicType("companies", {
          id: companyId
        }),
        user: {
          id: userId
        }
      }), {
        noDefaultParams: true,
        options
      });
    });

    _defineProperty(this, "deleteEmployee", _ref4 => {
      let {
        companyId,
        id,
        options
      } = _ref4;
      return this.del(`${this.resourcePath}/${companyId}/employees/${id}`, {
        options
      });
    });

    _defineProperty(this, "merge", _ref5 => {
      let {
        companyId,
        data,
        options
      } = _ref5;
      return this.post(`${this.resourcePath}/${companyId}/merge`, companyMergeSerializer(data), {
        noDefaultParams: true,
        options
      });
    });
  }

  get resourcePath() {
    return "v1/companies";
  }

  get serializer() {
    return companySerializer;
  }

}

var Company$1 = Company;

const trackDocumentProgress = onProgress => event => {
  if (!onProgress) return;
  const bytesTotal = event.total;
  const bytesUploaded = event.loaded;
  const progress = {
    bytesTotal,
    bytesUploaded,
    progress: Math.floor(bytesUploaded * 100 / bytesTotal)
  };
  onProgress(progress);
};

const noOp$1 = () => {};

class CompanyDocument extends LegacyDocumentResource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "create", _ref => {
      let {
        companyId,
        document,
        onProgress = noOp$1
      } = _ref;
      const {
        originalFile,
        contentType,
        title
      } = document;
      const encodedTitle = encodeURIComponent(title);
      return this.put("v1/documents", originalFile, {
        headers: {
          "Content-Type": contentType
        },
        options: {
          "company-id": companyId,
          title: encodedTitle
        },
        transformRequest: [data => data, data => data],
        onUploadProgress: trackDocumentProgress(onProgress)
      });
    });

    _defineProperty(this, "allForTender", _ref2 => {
      let {
        tenderId,
        options
      } = _ref2;
      return this.all({
        options: merge({
          filter: {
            "tender-id": tenderId
          },
          include: ["uploader"]
        }, options)
      });
    });

    _defineProperty(this, "createForInquiry", _ref3 => {
      let {
        companyId,
        inquiryId,
        document
      } = _ref3;
      return this.create({
        companyId,
        document
      }).then(response => {
        const {
          id: documentId
        } = response.data.data;
        return this.client.InquiryDocumentRelationship.create({
          documentId,
          inquiryId
        });
      });
    });
  }

  get resourcePath() {
    return "v1/company-documents";
  }

}

var CompanyDocument$1 = CompanyDocument;

class CompanySuggestion extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "search", _ref => {
      let {
        query,
        options
      } = _ref;
      return this.get(this.resourcePath, {
        options: merge({
          query
        }, options)
      });
    });
  }

  get resourcePath() {
    return "v1/company-suggestions";
  }

}

var CompanySuggestion$1 = CompanySuggestion;

class CreditInformationSource extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "createRequest", _ref => {
      let {
        id,
        options
      } = _ref;
      return this.post(`${this.resourcePath}/${id}/requests`, {
        options
      });
    });
  }

  get resourcePath() {
    return "v1/credit-information-sources";
  }

  get serializer() {
    return creditInformationSourceSerializer;
  }

}

var CreditInformationSource$1 = CreditInformationSource;

class CreditReport extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "allForCompany", _ref => {
      let {
        companyId,
        options
      } = _ref;
      return this.all({
        options: merge({
          page: {
            size: 1
          },
          filter: {
            "company-id": companyId
          }
        }, options)
      });
    });
  }

  get resourcePath() {
    return "v1/credit-reports";
  }

}

var CreditReport$1 = CreditReport;

class Datacenter extends Resource$1 {
  /**
   * @private
   * @returns {string} Datacenter resource path
   */
  get resourcePath() {
    return "v1/datacenters";
  }
  /**
   * @private
   * @returns {function} Datacenter serializer
   */


  get serializer() {
    return datacenterSerializer;
  }

}

var Datacenter$1 = Datacenter;

const noOp = () => {};

class Document extends Resource$1 {
  constructor() {
    var _superprop_getAll = () => super.all,
        _this;

    super(...arguments);
    _this = this;

    _defineProperty(this, "all", function () {
      let {
        options
      } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return _superprop_getAll().call(_this, {
        options: merge({
          page: {
            size: 999
          }
        }, options)
      });
    });

    _defineProperty(this, "create", requestOptions => {
      const {
        companyId,
        classificationTags,
        document: {
          originalFile,
          contentType,
          title
        },
        ownerId,
        ownerType,
        uploaderId,
        virtualPath,
        onProgress = noOp,
        options
      } = requestOptions;
      return this.put(this.resourcePath, originalFile, {
        headers: {
          "Content-Type": contentType
        },
        options: { ...(uploaderId && {
            "uploader-id": uploaderId
          }),
          "classification-tags": classificationTags,
          ...(companyId && {
            "company-id": companyId
          }),
          ...(ownerId && {
            "owner-id": ownerId
          }),
          ...(ownerType && {
            "owner-type": ownerType
          }),
          title: encodeURIComponent(title),
          "virtual-path": encodeURIComponent(virtualPath)
        },
        transformRequest: [data => data, data => data],
        onUploadProgress: trackDocumentProgress(onProgress),
        ...options
      });
    });
  }

  get resourcePath() {
    return "v1/documents";
  }

  get serializer() {
    return documentSerializer;
  }

}

var Document$1 = Document;

class DocumentKind extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "matches", _ref => {
      let {
        bankId,
        bankGroupId,
        inquiryId,
        options
      } = _ref;
      return this.get(`${this.resourcePath}/matches`, {
        options: {
          "bank-id": bankId,
          "bank-group-id": bankGroupId,
          "inquiry-id": inquiryId,
          ...options
        }
      });
    });
  }

  get resourcePath() {
    return "v1/document-kinds";
  }

  get serializer() {
    return documentKindSerializer;
  }

}

var DocumentKind$1 = DocumentKind;

class Email extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "isAvailable", this.find);

    _defineProperty(this, "verify", _ref => {
      let {
        data
      } = _ref;
      return this.post(`${this.resourcePath}/verifications`, emailVerificationSerializer(data), {
        noDefaultParams: true
      });
    });

    _defineProperty(this, "requestVerification", _ref2 => {
      let {
        data,
        contextId,
        contextType = "inquiries"
      } = _ref2;
      return this.post(`${this.resourcePath}/verification-requests`, emailVerificationRequestSerializer({ ...data,
        context: withPolymorphicType(contextType, {
          id: contextId
        })
      }), {
        noDefaultParams: true
      });
    });
  }

  get resourcePath() {
    return "v1/emails";
  }

}

var Email$1 = Email;

class EstimatedOfferConditions extends Resource$1 {
  get resourcePath() {
    return "v1/estimated-offer-conditions";
  }

  get serializer() {
    return estimatedOfferConditionsSerializer;
  }

}

var EstimatedOfferConditions$1 = EstimatedOfferConditions;

class ExpectedFinancialStatement extends Resource$1 {
  get resourcePath() {
    return "v1/expected-financial-statements";
  }

}

var ExpectedFinancialStatement$1 = ExpectedFinancialStatement;

class ExternalBusinessRelationship extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "resourcePath", companyId => `v1/companies/${companyId}/external-business-relationships`);

    _defineProperty(this, "all", _ref => {
      let {
        companyId,
        options
      } = _ref;
      return this.get(this.resourcePath(companyId), {
        options
      });
    });

    _defineProperty(this, "create", _ref2 => {
      let {
        companyId,
        bankId,
        options
      } = _ref2;
      return this.post(this.resourcePath(companyId), this.serializer({
        company: {
          id: companyId
        },
        bank: {
          id: bankId
        }
      }), {
        options
      });
    });

    _defineProperty(this, "delete", _ref3 => {
      let {
        companyId,
        id,
        options
      } = _ref3;
      return this.del(`${this.resourcePath(companyId)}/${id}`, {
        options
      });
    });
  }

  get serializer() {
    return externalBusinessRelationshipSerializer;
  }

}

var ExternalBusinessRelationship$1 = ExternalBusinessRelationship;

class IndicativeOffer extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "all", _ref => {
      let {
        transactionId,
        options
      } = _ref;
      return this.get(`${this.resourcePath}/calculate?transactionId=${transactionId}`, {
        options
      });
    });

    _defineProperty(this, "calculate", _ref2 => {
      let {
        data,
        options
      } = _ref2;
      return this.post(`${this.resourcePath}/calculate`, this.serializer(data), {
        options
      });
    });

    _defineProperty(this, "forward", _ref3 => {
      let {
        data,
        options
      } = _ref3;
      return this.post(`${this.resourcePath}/forwardings`, this.forwardingSerializer(data), {
        options
      });
    });

    _defineProperty(this, "convert", _ref4 => {
      let {
        id,
        data,
        options
      } = _ref4;
      return this.post(`${this.resourcePath}/${id}/convert`, this.serializer(data), {
        options
      });
    });
  }

  get resourcePath() {
    return "v1/indicative-offers";
  }

  get serializer() {
    return indicativeOfferSerializer;
  }

  get forwardingSerializer() {
    return indicativeOfferForwardingSerializer;
  }

}

var IndicativeOffer$1 = IndicativeOffer;

class IndividualAccess extends Resource$1 {
  get resourcePath() {
    return "v1/individual-accesses";
  }

  get serializer() {
    return individualAccessSerializer;
  }

}

var IndividualAccess$1 = IndividualAccess;

const idToDescriptor = id => ({
  id
});

class Inquiry extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "duplicate", _ref => {
      let {
        id,
        options
      } = _ref;
      return this.post(`${this.resourcePath}/${id}/duplicate`, {
        options
      });
    });

    _defineProperty(this, "complete", _ref2 => {
      let {
        id,
        offersToAcceptIds,
        contactDetails,
        options
      } = _ref2;
      return this.patch(`${this.resourcePath}/${id}/complete`, inquiryCompleteEventSerializer({ ...contactDetails,
        offersToAccept: offersToAcceptIds.map(idToDescriptor)
      }), {
        options
      });
    });

    _defineProperty(this, "history", _ref3 => {
      let {
        id,
        options
      } = _ref3;
      return this.get(`v1/histories/inquiries/${id}`, {
        options
      });
    });

    _defineProperty(this, "requiredDocuments", _ref4 => {
      let {
        id,
        options
      } = _ref4;
      return this.get(`${this.resourcePath}/${id}/required-documents`, {
        options
      });
    });

    _defineProperty(this, "requestedDocumentKinds", _ref5 => {
      let {
        id,
        options
      } = _ref5;
      return this.get(`${this.resourcePath}/${id}/requested-document-kinds`, {
        options
      });
    });

    _defineProperty(this, "allRiscKpiResults", _ref6 => {
      let {
        id,
        options
      } = _ref6;
      return this.get(`${this.resourcePath}/${id}/risc-kpi-results`, {
        options
      });
    });

    _defineProperty(this, "allRiscResults", _ref7 => {
      let {
        id,
        options
      } = _ref7;
      return this.get(`${this.resourcePath}/${id}/risc-results`, {
        options
      });
    });

    _defineProperty(this, "getRiskProfile", _ref8 => {
      let {
        id,
        bankId,
        options
      } = _ref8;
      return this.get(`${this.resourcePath}/${id}/risk-profile`, {
        options: {
          bankId: bankId,
          ...options
        }
      });
    });

    _defineProperty(this, "getEvents", _ref9 => {
      let {
        id,
        options
      } = _ref9;
      return this.get(`${this.resourcePath}/${id}/events`, {
        options
      });
    });

    _defineProperty(this, "allVisibleBankGroups", _ref10 => {
      let {
        id,
        options
      } = _ref10;
      return this.get(`${this.resourcePath}/${id}/bank-groups`, {
        options: merge({
          page: {
            size: 999
          }
        }, options)
      });
    });

    _defineProperty(this, "allBankGroupCandidates", _ref11 => {
      let {
        id,
        options
      } = _ref11;
      return this.get(`${this.resourcePath}/${id}/bank-group-candidates`, {
        options: merge({
          page: {
            size: 999
          }
        }, options)
      });
    });

    _defineProperty(this, "createBankGroupCandidate", _ref12 => {
      let {
        id,
        data,
        options
      } = _ref12;
      return this.post(`${this.resourcePath}/${id}/bank-group-candidates`, bankGroupCandidateSerializer(data), {
        options
      });
    });

    _defineProperty(this, "getExternalState", _ref13 => {
      let {
        id,
        options
      } = _ref13;
      return this.get(`${this.resourcePath}/${id}/state`, {
        options
      });
    });

    _defineProperty(this, "updateExternalState", _ref14 => {
      let {
        id,
        data,
        options
      } = _ref14;
      return this.patch(`${this.resourcePath}/${id}/state`, inquiryExternalStateSerializer(data), {
        options
      });
    });

    _defineProperty(this, "getSuccessFee", _ref15 => {
      let {
        id,
        options
      } = _ref15;
      return this.get(`${this.resourcePath}/${id}/success-fee`, {
        options
      });
    });

    _defineProperty(this, "setSuccessFee", _ref16 => {
      let {
        id,
        data,
        options
      } = _ref16;
      return this.put(`${this.resourcePath}/${id}/success-fee`, successFeeSerializer(data), {
        options
      });
    });

    _defineProperty(this, "acceptSuccessFee", _ref17 => {
      let {
        id,
        options
      } = _ref17;
      return this.patch(`${this.resourcePath}/${id}/success-fee`, successFeeSerializer({
        acceptedAt: new Date()
      }), {
        options
      });
    });

    _defineProperty(this, "claim", _ref18 => {
      let {
        id,
        options
      } = _ref18;
      return this.patch(`${this.resourcePath}/${id}/claim`, null, {
        options
      });
    });

    _defineProperty(this, "notifyBankLostOffer", _ref19 => {
      let {
        id,
        offerId,
        options
      } = _ref19;
      return this.post(`${this.resourcePath}/${id}/lost_for_bank/${offerId}`, {}, {
        options
      });
    });
  }

  get resourcePath() {
    return "v1/inquiries";
  }

  get serializer() {
    return inquirySerializer;
  }

}

var Inquiry$1 = Inquiry;

class InquiryDocumentRelationship extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "resourcePath", inquiryId => `v1/inquiries/${inquiryId}/relationships/documents`);

    _defineProperty(this, "all", _ref => {
      let {
        inquiryId,
        options
      } = _ref;
      return this.get(this.resourcePath(inquiryId), {
        options
      });
    });

    _defineProperty(this, "create", _ref2 => {
      let {
        documentId,
        inquiryId,
        options
      } = _ref2;
      return this.post(this.resourcePath(inquiryId), this.serializer([{
        id: documentId
      }]), {
        noDefaultParams: true,
        options
      });
    });

    _defineProperty(this, "delete", _ref3 => {
      let {
        documentId,
        inquiryId,
        options
      } = _ref3;
      return this.del(this.resourcePath(inquiryId), {
        data: this.serializer([{
          id: documentId
        }]),
        noDefaultParams: true,
        options
      });
    });
  }

  get serializer() {
    return documentRelationshipSerializer;
  }

}

var InquiryDocumentRelationship$1 = InquiryDocumentRelationship;

class LifecycleLogEntry extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "adminDocumentDownload", _ref => {
      let {
        data,
        options
      } = _ref;
      return this.create({
        data: { ...data,
          eventType: "admin_document_download"
        },
        options
      });
    });

    _defineProperty(this, "offerDocumentDownload", _ref2 => {
      let {
        data,
        options
      } = _ref2;
      return this.create({
        data: { ...data,
          eventType: "offer_document_download"
        },
        options
      });
    });

    _defineProperty(this, "startXbrl", _ref3 => {
      let {
        data,
        options
      } = _ref3;
      return this.create({
        data: { ...data,
          eventType: "start_xbrl"
        },
        options
      });
    });

    _defineProperty(this, "viewOfferDetails", _ref4 => {
      let {
        data,
        options
      } = _ref4;
      return this.create({
        data: { ...data,
          eventType: "view_offer_details"
        },
        options
      });
    });

    _defineProperty(this, "viewOffers", _ref5 => {
      let {
        data,
        options
      } = _ref5;
      return this.create({
        data: { ...data,
          eventType: "view_offers"
        },
        options
      });
    });
  }

  get resourcePath() {
    return "v1/lifecycle-log-entries";
  }

  get serializer() {
    return lifecycleLogEntrySerializer;
  }

}

var LifecycleLogEntry$1 = LifecycleLogEntry;

class Membership extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "resourcePath", (organizationType, organizationId) => `v1/${organizationType}/${organizationId}/memberships`);

    _defineProperty(this, "serializer", _ref => {
      let {
        data,
        organizationType,
        organizationId
      } = _ref;
      return membershipSerializer({ ...data,
        organization: withPolymorphicType(organizationType, {
          id: organizationId
        })
      });
    });

    _defineProperty(this, "all", _ref2 => {
      let {
        organizationType,
        organizationId,
        options
      } = _ref2;
      return this.get(this.resourcePath(organizationType, organizationId), {
        options: merge({
          page: {
            size: 999
          }
        }, options)
      });
    });

    _defineProperty(this, "create", _ref3 => {
      let {
        data,
        options,
        organizationType,
        organizationId
      } = _ref3;
      return this.post(this.resourcePath(organizationType, organizationId), this.serializer({
        data,
        organizationType,
        organizationId
      }), {
        options
      });
    });

    _defineProperty(this, "delete", _ref4 => {
      let {
        options,
        organizationType,
        organizationId,
        id
      } = _ref4;
      return this.del(`${this.resourcePath(organizationType, organizationId)}/${id}`, {
        options
      });
    });

    _defineProperty(this, "update", _ref5 => {
      let {
        data,
        id,
        options,
        organizationType,
        organizationId
      } = _ref5;
      return this.patch(`${this.resourcePath(organizationType, organizationId)}/${id}`, this.serializer({
        data,
        organizationType,
        organizationId
      }), {
        options
      });
    });
  }

}

var Membership$1 = Membership;

class MultiplierOrganization extends Resource$1 {
  get resourcePath() {
    return "v1/multiplier-organizations";
  }

  get serializer() {
    return multiplierOrganizationSerializer;
  }

}

var MultiplierOrganization$1 = MultiplierOrganization;

class Offer extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "allForTender", _ref => {
      let {
        tenderId,
        options
      } = _ref;
      return this.all({
        options: merge({
          filter: {
            "tender-id": tenderId
          }
        }, options)
      });
    });

    _defineProperty(this, "fireEvent", _ref2 => {
      let {
        id,
        event,
        data,
        options
      } = _ref2;
      return this.patch(`${this.resourcePath}/${id}/${event}`, eventPayload(data), {
        options
      });
    });

    _defineProperty(this, "history", _ref3 => {
      let {
        id,
        options
      } = _ref3;
      return this.get(`${this.resourcePath}/${id}/history-entries`, {
        options
      });
    });

    _defineProperty(this, "getSuccessFee", _ref4 => {
      let {
        id,
        options
      } = _ref4;
      return this.get(`${this.resourcePath}/${id}/success-fee`, {
        options
      });
    });

    _defineProperty(this, "setSuccessFee", _ref5 => {
      let {
        id,
        data,
        options
      } = _ref5;
      return this.put(`${this.resourcePath}/${id}/success-fee`, successFeeSerializer(data), {
        options
      });
    });

    _defineProperty(this, "acceptSuccessFee", _ref6 => {
      let {
        id,
        options
      } = _ref6;
      return this.patch(`${this.resourcePath}/${id}/success-fee`, successFeeSerializer({
        acceptedAt: new Date()
      }), {
        options
      });
    });
  }

  get resourcePath() {
    return "v1/offers";
  }

  get serializer() {
    return offerSerializer;
  }

}

const eventPayload = data => {
  if (!(data && Object.keys(data).length)) return {};
  return {
    data: {
      type: "offer-state-events",
      attributes: data
    }
  };
};

var Offer$1 = Offer;

class OfferDocument extends LegacyDocumentResource$1 {
  get resourcePath() {
    return "v1/offer-documents";
  }

}

var OfferDocument$1 = OfferDocument;

class ProductConditions extends Resource$1 {
  get serializer() {
    return productConditionsSerializer;
  }

  get resourcePath() {
    return "v1/product-conditions";
  }

}

var ProductConditions$1 = ProductConditions;

/**
 * Questionnaire Resource
 */

class Questionnaire extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "allItems", _ref => {
      let {
        id,
        options
      } = _ref;
      return this.get(`${this.resourcePath}/${id}/items`, {
        options: merge({
          page: {
            size: 9999
          }
        }, options)
      });
    });

    _defineProperty(this, "createItem", _ref2 => {
      let {
        questionnaireId,
        data,
        options
      } = _ref2;
      return this.post(`${this.resourcePath}/${questionnaireId}/items`, questionnaireItemSerializer(data), {
        noDefaultParams: true,
        options
      });
    });

    _defineProperty(this, "updateItem", _ref3 => {
      let {
        questionnaireId,
        id,
        data,
        options
      } = _ref3;
      return this.patch(`${this.resourcePath}/${questionnaireId}/items/${id}`, questionnaireItemSerializer(data), {
        noDefaultParams: true,
        options
      });
    });

    _defineProperty(this, "deleteItem", _ref4 => {
      let {
        questionnaireId,
        id,
        options
      } = _ref4;
      return this.del(`${this.resourcePath}/${questionnaireId}/items/${id}`, {
        noDefaultParams: true,
        options
      });
    });
  }

  /**
   * @returns {string} questionnaire resource path
   */
  get resourcePath() {
    return "v1/questionnaires";
  }
  /**
   * @returns {function} questionnaire serializer
   */


  get serializer() {
    return questionnaireSerializer;
  }
  /**
   * Get all items for a specific questionnaire
   * @param {object} config
   * @param {string} config.id The Id of the questionnaire to get the items for
   * @param {object} config.options Options that are forwarded to the http client
   * @returns {Promise{object}}
   */


}

var Questionnaire$1 = Questionnaire;

class RequestedDocumentKind extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "resourcePath", inquiryId => `v1/inquiries/${inquiryId}/requested-document-kinds`);

    _defineProperty(this, "all", _ref => {
      let {
        inquiryId,
        options
      } = _ref;
      return this.get(this.resourcePath(inquiryId), {
        options
      });
    });

    _defineProperty(this, "find", _ref2 => {
      let {
        inquiryId,
        id,
        options
      } = _ref2;
      return this.get(`${this.resourcePath(inquiryId)}/${id}`, {
        options
      });
    });

    _defineProperty(this, "update", _ref3 => {
      let {
        inquiryId,
        id,
        data,
        options
      } = _ref3;
      return this.patch(`${this.resourcePath(inquiryId)}/${id}`, this.serializer(data), {
        options
      });
    });

    _defineProperty(this, "create", _ref4 => {
      let {
        inquiryId,
        data,
        options
      } = _ref4;
      return this.post(`${this.resourcePath(inquiryId)}`, this.serializer(data), {
        options
      });
    });

    _defineProperty(this, "delete", _ref5 => {
      let {
        inquiryId,
        id,
        options
      } = _ref5;
      return this.del(`${this.resourcePath(inquiryId)}/${id}`, {
        options
      });
    });
  }

  get serializer() {
    return requestedDocumentKindSerializer;
  }

}

var RequestedDocumentKind$1 = RequestedDocumentKind;

class RequestedDocumentsSet extends Resource$1 {
  get resourcePath() {
    return "v1/requested-documents-sets";
  }

  get serializer() {
    return requestedDocumentsSetSerializer;
  }

}

var RequestedDocumentsSet$1 = RequestedDocumentsSet;

class SuccessFeeAcceptance extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "create", _ref => {
      let {
        data,
        options
      } = _ref;
      return this.post(this.resourcePath, successFeeAcceptanceSerializer(data), {
        options
      });
    });
  }

  get resourcePath() {
    return "v1/success-fee-acceptance";
  }

}

var SuccessFeeAcceptance$1 = SuccessFeeAcceptance;

class SubsidyProduct extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "all", _ref => {
      let {
        latest,
        name,
        enhancedProcess,
        fingerprint,
        options
      } = _ref;
      return this.get(this.resourcePath, {
        options: merge({
          filter: {
            "enhanced-process": enhancedProcess,
            fingerprint,
            latest,
            name
          }
        }, options)
      });
    });

    _defineProperty(this, "find", _ref2 => {
      let {
        id,
        options
      } = _ref2;
      return this.get(`${this.resourcePath}/${id}`, {
        noDefaultParams: true,
        options
      });
    });
  }

  get resourcePath() {
    return "v1/subsidy-products";
  }

}

var SubsidyProduct$1 = SubsidyProduct;

class SubsidyRequest extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "create", _ref => {
      let {
        data,
        options
      } = _ref;
      return this.post(this.resourcePath, this.inquirySerializer(data), {
        options: merge({
          include: ["subsidy-products"]
        }, options)
      });
    });

    _defineProperty(this, "find", _ref2 => {
      let {
        id,
        options
      } = _ref2;
      return super.find({
        id,
        options: merge({
          include: ["subsidy-products"]
        }, options)
      });
    });
  }

  get resourcePath() {
    return "v1/subsidy-requests";
  }

  get serializer() {
    return subsidyRequestSerializer;
  }

  get inquirySerializer() {
    return inquirySerializer;
  }

}

var SubsidyRequest$1 = SubsidyRequest;

class Task extends Resource$1 {
  get resourcePath() {
    return "v1/tasks";
  }

  get serializer() {
    return taskSerializer;
  }

}

var Task$1 = Task;

class TaxAccountant extends Resource$1 {
  get resourcePath() {
    return "v1/tax-accountants";
  }

  get serializer() {
    return taxAccountantSerializer;
  }

}

var TaxAccountant$1 = TaxAccountant;

/**
 * Tender resource
 */

class Tender extends Resource$1 {
  /**
   * @private
   * @returns {string} Tender resource path
   */
  get resourcePath() {
    return "v1/tenders";
  }
  /**
   * @private
   * @returns {function} Tender serializer
   */


  get serializer() {
    return tenderSerializer;
  }

}

var Tender$1 = Tender;

class TermsAndConditions extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "current", _ref => {
      let {
        options
      } = _ref;
      return this.get(`${this.resourcePath}/current`, {
        noDefaultParams: true,
        options
      });
    });
  }

  get resourcePath() {
    return "v1/terms-and-conditions";
  }

  get serializer() {
    return termsAndConditionsVersionsSerializer;
  }

}

var TermsAndConditions$1 = TermsAndConditions;

class User extends Resource$1 {
  constructor() {
    var _this;

    super(...arguments);
    _this = this;

    _defineProperty(this, "acceptTermsAndConditions", _ref => {
      let {
        id,
        options
      } = _ref;
      return this.post(`${this.resourcePath}/${id}/accept-terms-and-conditions`, null, {
        noDefaultParams: true,
        options
      });
    });

    _defineProperty(this, "generateOneTimePassword", _ref2 => {
      let {
        id,
        options
      } = _ref2;
      return this.post(`${this.resourcePath}/${id}/generate-one-time-password`, null, {
        noDefaultParams: true,
        options
      });
    });

    _defineProperty(this, "memberships", _ref3 => {
      let {
        id,
        options
      } = _ref3;
      return this.get(`${this.resourcePath}/${id}/memberships`, {
        options: merge({
          include: ["bank", "company"]
        }, options)
      });
    });

    _defineProperty(this, "confirm", _ref4 => {
      let {
        id,
        data,
        options
      } = _ref4;
      return this.put(`${this.resourcePath}/${id}/confirm`, data, {
        options
      });
    });

    _defineProperty(this, "isDeletable", _ref5 => {
      let {
        id,
        options
      } = _ref5;
      return this.get(`${this.resourcePath}/${id}/deletable`, {
        options
      });
    });

    _defineProperty(this, "current", function () {
      let {
        options
      } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return _this.get("v1/identity", {
        options
      });
    });

    _defineProperty(this, "requestEmailVerification", _ref6 => {
      let {
        data,
        options
      } = _ref6;
      return this.post("v1/verification/email", emailVerificationSerializer(data), {
        options
      });
    });

    _defineProperty(this, "verifyEmail", _ref7 => {
      let {
        data,
        options
      } = _ref7;
      return this.post("v1/verification/email/verify", emailVerificationSerializer(data), {
        options
      });
    });

    _defineProperty(this, "requestTelephoneVerification", _ref8 => {
      let {
        data,
        options
      } = _ref8;
      return this.post("v1/verification/telephone", telephoneVerificationSerializer(data), {
        options
      });
    });

    _defineProperty(this, "verifyTelephone", _ref9 => {
      let {
        data,
        options
      } = _ref9;
      return this.post("v1/verification/telephone/verify", telephoneVerificationSerializer(data), {
        options
      });
    });
  }

  get resourcePath() {
    return "v1/users";
  }

  get serializer() {
    return userSerializer;
  }

}

var User$1 = User;

class Vehicle extends Resource$1 {
  constructor() {
    super(...arguments);

    _defineProperty(this, "resourcePath", tenderId => `v1/tenders/${tenderId}/vehicles`);

    _defineProperty(this, "create", _ref => {
      let {
        tenderId,
        data,
        options
      } = _ref;
      return this.post(this.resourcePath(tenderId), this.serializer(data), {
        options
      });
    });

    _defineProperty(this, "update", _ref2 => {
      let {
        tenderId,
        id,
        data,
        options
      } = _ref2;
      return this.patch(`${this.resourcePath(tenderId)}/${id}`, this.serializer(data), {
        options
      });
    });

    _defineProperty(this, "delete", _ref3 => {
      let {
        tenderId,
        id,
        options
      } = _ref3;
      return this.del(`${this.resourcePath(tenderId)}/${id}`, {
        options
      });
    });
  }

  get serializer() {
    return vehicleSerializer;
  }

}

var Vehicle$1 = Vehicle;

class VirtualPath extends Resource$1 {
  get resourcePath() {
    return "v1/virtual-paths";
  }

  get serializer() {
    return virtualPathSerializer;
  }

  update(_ref) {
    let {
      ownerId,
      ownerType,
      virtualPath,
      newVirtualPath,
      options
    } = _ref;
    const encodedOldVirtualPath = encodeURIComponent(virtualPath);
    const oldPathInBase64 = btoa(virtualPath);
    return this.patch(`${this.resourcePath}/${ownerType}/${ownerId}/${encodedOldVirtualPath}`, this.serializer({
      id: `${ownerType}-${ownerId}-${oldPathInBase64}`,
      value: newVirtualPath
    }), {
      noDefaultParams: true,
      options: merge({
        include: ["documents"]
      }, options)
    });
  }

}

var VirtualPath$1 = VirtualPath;

var resources = {
  AdminDocument: AdminDocument$1,
  Attachment: Attachment$1,
  Authentication: Authentication$1,
  Bank: Bank$1,
  BankCode: BankCode$1,
  BankGroup: BankGroup$1,
  BankGroupCandidate: BankGroupCandidate$1,
  BankInquiryNote: BankInquiryNote$1,
  BankTag: BankTag$1,
  BankVisibility: BankVisibility$1,
  BankVisibilityParameterSet: BankVisibilityParameterSet$1,
  Company: Company$1,
  CompanyDocument: CompanyDocument$1,
  CompanySuggestion: CompanySuggestion$1,
  CreditInformationSource: CreditInformationSource$1,
  CreditReport: CreditReport$1,
  Datacenter: Datacenter$1,
  Document: Document$1,
  DocumentKind: DocumentKind$1,
  Email: Email$1,
  EstimatedOfferConditions: EstimatedOfferConditions$1,
  ExpectedFinancialStatement: ExpectedFinancialStatement$1,
  ExternalBusinessRelationship: ExternalBusinessRelationship$1,
  IndicativeOffer: IndicativeOffer$1,
  IndividualAccess: IndividualAccess$1,
  Inquiry: Inquiry$1,
  InquiryDocumentRelationship: InquiryDocumentRelationship$1,
  LifecycleLogEntry: LifecycleLogEntry$1,
  Membership: Membership$1,
  MultiplierOrganization: MultiplierOrganization$1,
  Offer: Offer$1,
  OfferDocument: OfferDocument$1,
  ProductConditions: ProductConditions$1,
  Questionnaire: Questionnaire$1,
  RequestedDocumentKind: RequestedDocumentKind$1,
  RequestedDocumentsSet: RequestedDocumentsSet$1,
  SuccessFeeAcceptance: SuccessFeeAcceptance$1,
  SubsidyProduct: SubsidyProduct$1,
  SubsidyRequest: SubsidyRequest$1,
  Task: Task$1,
  TaxAccountant: TaxAccountant$1,
  Tender: Tender$1,
  TermsAndConditions: TermsAndConditions$1,
  User: User$1,
  Vehicle: Vehicle$1,
  VirtualPath: VirtualPath$1
};

class CompeonAPI {
  constructor(config) {
    _defineProperty(this, "ensureAuthenticated", async redirectParams => {
      const {
        code,
        state
      } = parseAuthQueryParams();
      const isReturningFromRedirect = code && state && this.authState.isValid(state);

      if (!this.isAuthenticated && !isReturningFromRedirect) {
        return redirectToLogin({ ...this.config.authConfig,
          redirectParams,
          authState: this.authState
        });
      }

      await this.obtainTokens(isReturningFromRedirect, code);
      this.refreshTimeout = setTimeout(this.ensureAuthenticated, tokenRefreshTimeout(this.expiresIn));
    });

    _defineProperty(this, "obtainTokens", async (isInitialTokenFetch, code) => {
      const {
        fetchTokens,
        refreshTokens
      } = this.Authentication;
      const tokenResponse = isInitialTokenFetch ? await fetchTokens({
        code
      }) : await refreshTokens({
        refreshCode: this.refreshToken
      });
      let token = null;
      let refreshData = {
        refreshToken: null,
        expiresAt: null,
        identityToken: null
      };

      if (tokenResponse.status === 401) {
        const {
          handleInvalidSession
        } = this.config.authConfig;
        if (typeof handleInvalidSession === "function") handleInvalidSession();
      } else {
        ({
          token,
          ...refreshData
        } = tokenResponse.data);
      }

      this.tokenHeader = token;
      Object.assign(this, refreshData);
    });

    _defineProperty(this, "registerLogoutCheck", () => {
      this.logoutInterval = checkLogout(this.logout, this.config.authConfig);
    });

    _defineProperty(this, "logout", () => {
      this.stopTokenRefresh();
      completeLogout(this.config.authConfig);
    });

    _defineProperty(this, "stopTokenRefresh", () => {
      clearTimeout(this.refreshTimeout);
      clearInterval(this.logoutInterval);
      this.tokenHeader = undefined;
      delete this.refreshToken;
      delete this.refreshTimeout;
      delete this.logoutInterval;
    });

    this.config = config;
    this.httpClient = axios.create(axiosConfig(config));
    this.httpClient.interceptors.request.use(prepareParams, handleError);

    for (const resource in resources) {
      this[resource] = new resources[resource](this);
    }

    const {
      skipAuthentication,
      skipLogout,
      authConfig = {}
    } = this.config;

    if (!skipAuthentication) {
      this.authState = authConfig.authState || cookieAuthState();
      this.authPromise = this.ensureAuthenticated();

      if (authConfig.withCookie) {
        this.authPromise.then(this.Authentication.getCookie);
      }

      if (!skipLogout) {
        this.authPromise.then(this.registerLogoutCheck);
      }
    }
  }

  get defaultHeaders() {
    return this.httpClient.defaults.headers;
  }

  get tokenHeader() {
    return this.defaultHeaders.Authorization;
  }

  set tokenHeader(token) {
    if (token) this.defaultHeaders.Authorization = `token ${token}`;else delete this.defaultHeaders.Authorization;
  }

  get isAuthenticated() {
    return this.tokenHeader && this.refreshToken && this.expiresAt && this.expiresAt >= new Date();
  }

}

export { cookieAuthState, CompeonAPI as default, sessionStorageAuthState };
